import { useEffect, useState } from "react";
import LinkCardItems from "./linkCardItems";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiClient } from "../../context/ApiContext";
import { getMySiteFavoriteLinks, getMySiteLinks, getMySiteRecentLinks } from "../../services/mysite/MySiteHome";
import { LinkCard } from "../../types/home/links";

interface ILinksMenuProps {
    loginName: string
}

type LinksTab = "recentLinks" | "allLinks" | "favoriteLinks";

const LinksMenu = (props: ILinksMenuProps) => {
    const [selectedTab, setSelectedTab] = useState<LinksTab>("allLinks");
    const [links, setLinks] = useState<LinkCard[] | null>(null);
    const [favouriteLinks, setFavouriteLinks] = useState<LinkCard[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const api = useApiClient();

    // Refetch on every tab change (the GraphQL version was network-only) so a star
    // toggled on one tab is reflected on the next. Favourites always ride along
    // because every tab needs them for the star state.
    useEffect(() => {
        if (!props.loginName) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        const favourites = getMySiteFavoriteLinks(props.loginName, api);
        const list =
            selectedTab === "recentLinks"
                ? getMySiteRecentLinks(props.loginName, api)
                : selectedTab === "allLinks"
                    ? getMySiteLinks(props.loginName, api)
                    : favourites;

        Promise.all([list, favourites])
            .then(([listResponse, favouritesResponse]) => {
                if (cancelled) return;
                setLinks(listResponse.data ?? []);
                setFavouriteLinks(favouritesResponse.data ?? []);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Error fetching links:', err);
                setError(err?.message || 'Unable to load links');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [selectedTab, props.loginName, api]);

    if (error) {
        return <p>Error: {error}</p>;
    }

    return (
        <Tabs value={selectedTab} onValueChange={(value) => setSelectedTab(value as LinksTab)} className='links-menu'>
            <TabsList className='mb-2 links-tab'>
                <TabsTrigger className="recent-links" value="recentLinks">Recent Links</TabsTrigger>
                <TabsTrigger className="all-links" value="allLinks">All Links</TabsTrigger>
                <TabsTrigger className="favorite-links" value="favoriteLinks">Favorites</TabsTrigger>
            </TabsList>

            {loading && (
                <div className="flex flex-col gap-5 p-2 mt-2">
                    {Array.from({ length: 9 }).map((_, i) => (
                        <Skeleton key={i} className="w-full h-[50px]" />
                    ))}
                </div>
            )}

            {!loading && links && selectedTab === "recentLinks" && (
                <LinkCardItems categorize={false} links={links} favouriteLinks={favouriteLinks} />
            )}
            {!loading && links && selectedTab === "allLinks" && (
                <LinkCardItems categorize={true} links={links} favouriteLinks={favouriteLinks} searchable={true} />
            )}
            {!loading && links && selectedTab === "favoriteLinks" && (
                <LinkCardItems categorize={false} links={links} favouriteLinks={favouriteLinks} />
            )}
        </Tabs>
    );
}

export default LinksMenu;
